import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import useWindowSize from 'react-use/lib/useWindowSize'
import { Annotations } from '@pdftron/webviewer'
import { useMutation, useQuery } from '@apollo/client'
import { message, Modal } from 'antd'
import updateImDfmFeedbackInfoMutation from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/ActiveSlide/updateImDfmFeedbackInfoMutation'
import { DFM_NEEDS_FICTIV_REVIEW } from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/constants'
import {
    SLIDE_QUERY_PARAM,
    MODE_QUERY_PARAM,
} from '@Admin/AdminQuotingPage/constants'
import {
    MODAL_MARGIN_WIDTH,
    MODAL_MIN_HEIGHT,
    MODAL_MIN_WIDTH,
} from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/constants'

import Box from '~/core/Box'
import { getUserIdentity } from '~/redux-modules/identity'
import { useExtractUrlSearchParams } from '~/lib/extractUrlSearchParams'
import getColor from '~/lib/getColor'
import logger from '~/lib/logger'

import CloseIcon from './CloseIcon'
import ImDfmModalHeader from './ImDfmModalHeader'
import ImDfmSlidesPanel from './ImDfmSlidesPanel'
import ImDfmViewerPanel from './ImDfmViewerPanel'
import QmrNavigation from './QmrNavigation'
import SlideActionPanel from './SlideActionPanel'
import imDfmModalHeaderQuery from './ImDfmModalHeader/imDfmModalHeaderQuery'
import dfmFeedbackAttachmentQuery from './dfmFeedbackAttachmentQuery'
import getQmrAttachmentAnnotationsQuery from './getQmrAttachmentAnnotationsQuery'
import upsertDfmAttachmentAnnotationsMutation from './upsertDfmAttachmentAnnotationsMutation'
import styles from './styles.css'

const ANNOTATE_MODE = 'annotate'
const BORDER_STYLE = `1px solid ${getColor('blackAlpha15')}`

type ParamsType = {
    qmrId?: string
}

interface ImDfmModalPropsType {
    isImQuoteType: boolean
    onCancel: () => void
    qmrId?: string
    setIsModalVisible: (toggle: boolean) => void
    visible?: boolean
    orderId?: string
    refetch?: () => void
}

const ImDfmModal = (props: ImDfmModalPropsType) => {
    const {
        isImQuoteType,
        onCancel,
        visible,
        orderId,
        setIsModalVisible,
        refetch,
    } = props

    const { width: windowWidth, height: windowHeight } = useWindowSize()

    const { search } = useLocation()
    const { qmrId: urlQmrId } = useParams<ParamsType>()
    let qmrId = urlQmrId

    if (!qmrId) {
        qmrId = props.qmrId
    }

    const [attachmentId, setAttachmentId] = useState('')
    const [imDfmXfdf, setImDfmXfdf] = useState()
    const [isImDfmAnnoSaving, setIsImDfmAnnoSaving] = useState(false)
    const [unsavedImDfmAnnotations, setUnsavedImDfmAnnotations] = useState<
        Annotations.Annotation[]
    >([])

    const identity = useSelector(getUserIdentity)
    const userId = identity?.id

    const [upsertDfmAttachmentAnnotations] = useMutation(
        upsertDfmAttachmentAnnotationsMutation,
    )

    const [updateImDfmFeedbackInfo] = useMutation(
        updateImDfmFeedbackInfoMutation,
    )

    const searchParams = useExtractUrlSearchParams(search, [
        SLIDE_QUERY_PARAM,
        MODE_QUERY_PARAM,
    ])

    const dfmFeedbackId = searchParams[SLIDE_QUERY_PARAM]
    const isInAnnotationMode = searchParams[MODE_QUERY_PARAM] === ANNOTATE_MODE

    const { data: dfmData } = useQuery(dfmFeedbackAttachmentQuery, {
        variables: { dfmFeedbackId },
        skip: !isInAnnotationMode && !dfmFeedbackId,
    })

    const dfmDataAttachmentId = dfmData?.dfmFeedback?.attachment?.id

    const { data: qmrData } = useQuery(imDfmModalHeaderQuery, {
        variables: { id: qmrId },
        skip: !qmrId,
    })

    const qmr = qmrData?.quoteModelRevision

    const isCurrentQmrRevision =
        qmr?.version === qmr?.quoteModel?.selectedQuoteModelRevision?.version

    useEffect(() => {
        if (dfmDataAttachmentId) {
            setAttachmentId(dfmDataAttachmentId)
        }
    }, [dfmDataAttachmentId, setAttachmentId])

    const {
        data: attachmentAnnoData,
        refetch: refetchAttachmentAnnotationUrl,
    } = useQuery(getQmrAttachmentAnnotationsQuery, {
        variables: { attachmentId, quoteModelRevisionId: qmrId },
        skip: !attachmentId,
    })

    const qmrAttachmentAnnotationsUrl =
        attachmentAnnoData?.getQmrAttachmentAnnotations
            ?.annotationsDataDownloadUrl

    const updateStatusOnSaveAnnotations = async () => {
        try {
            await updateImDfmFeedbackInfo({
                variables: {
                    dfmFeedbackId,
                    status: DFM_NEEDS_FICTIV_REVIEW,
                },
            })
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: 'ImDfmModal::Failed to update status with updateDfmFeedbackInfo mutation',
                    dfmFeedbackId,
                    imDfmAttachmentId: attachmentId,
                    qmrId,
                    userId,
                },
            })
        }
    }

    const onSaveImDfmAnnotations = async () => {
        try {
            setIsImDfmAnnoSaving(true)

            const variables = {
                attachmentId,
                qmrAttachmentAnnotationsInput: {
                    data: imDfmXfdf,
                    userId,
                },
                newAnnotationsAdded: false,
            }

            if (imDfmXfdf) {
                const newAnnotations = unsavedImDfmAnnotations.filter(
                    unsavedImDfmAnnotation =>
                        unsavedImDfmAnnotation.IsAdded === true,
                )

                if (newAnnotations.length) {
                    variables.newAnnotationsAdded = true
                }

                await upsertDfmAttachmentAnnotations({
                    variables,
                })

                setUnsavedImDfmAnnotations([])

                await updateStatusOnSaveAnnotations()

                message.success('Annotations saved successfully')
            }
        } catch (err) {
            message.error('Failed to save annotations')
            logger.exception(err, {
                info: 'ImDfmModal-onSaveImDfmAnnotations::Failed on upsertDfmAttachmentAnnotationsMutation',
                qmrId,
                imDfmAttachmentId: attachmentId,
                userId,
            })
        } finally {
            await refetchAttachmentAnnotationUrl()
            setIsImDfmAnnoSaving(false)
        }
    }

    return (
        <Modal
            bodyStyle={{
                height: windowHeight - 140,
                marginTop: '-40px',
                minHeight: MODAL_MIN_HEIGHT,
                minWidth: MODAL_MIN_WIDTH,
                padding: 0,
            }}
            centered
            closable={orderId ? !isInAnnotationMode : false}
            closeIcon={!isInAnnotationMode && <CloseIcon />}
            footer={null}
            maskClosable
            onCancel={onCancel}
            style={{
                padding: 0,
            }}
            visible={visible}
            width={windowWidth - MODAL_MARGIN_WIDTH}
            wrapClassName={styles.imDfmModalWrapper}
        >
            <Box borderRadius="4px" direction="col" height="100%" width="100%">
                <ImDfmModalHeader
                    isImDfmAnnoSaving={isImDfmAnnoSaving}
                    isInAnnotationMode={isInAnnotationMode}
                    onSaveImDfmAnnotations={onSaveImDfmAnnotations}
                    orderId={orderId}
                    refetch={refetch}
                    setIsModalVisible={setIsModalVisible}
                    qmrId={qmrId}
                />
                <Box
                    direction="row"
                    height="100%"
                    style={{ overflowY: 'auto' }}
                >
                    {!isInAnnotationMode && (
                        <Box
                            backgroundColor="grey2"
                            borderRight={BORDER_STYLE}
                            direction="col"
                            style={{
                                overflowY: 'auto',
                                padding: '8px 8px 0 0',
                            }}
                            width="16%"
                        >
                            <Box>
                                <ImDfmSlidesPanel qmrId={qmrId} />
                            </Box>
                        </Box>
                    )}
                    <Box
                        minWidth="572px"
                        width={isInAnnotationMode ? '100%' : '61%'}
                    >
                        <ImDfmViewerPanel
                            attachmentId={attachmentId}
                            dfmFeedbackId={dfmFeedbackId}
                            qmrAttachmentAnnotationsUrl={
                                qmrAttachmentAnnotationsUrl
                            }
                            setImDfmXfdf={setImDfmXfdf}
                            setUnsavedImDfmAnnotations={
                                setUnsavedImDfmAnnotations
                            }
                            unsavedImDfmAnnotations={unsavedImDfmAnnotations}
                        />
                    </Box>
                    {!isInAnnotationMode && (
                        <Box
                            borderLeft={BORDER_STYLE}
                            minWidth="300px"
                            width="23%"
                        >
                            <SlideActionPanel isImQuoteType={isImQuoteType} />
                        </Box>
                    )}
                </Box>
            </Box>
            {isCurrentQmrRevision && <QmrNavigation />}
        </Modal>
    )
}

export default ImDfmModal
